import { daysUntil, expirationState } from "./stock.js";
import { normalizeFoodIdentity as canonicalFoodIdentity, normalizeFoodMatch } from "./foodIdentity.js";
import { resolveIngredientProductId } from "./shopping.js";
/**
 * Food-name folding lives in ONE place now (#1048). These two stay exported
 * from here because the meal assembler's callers reach for them by these names
 * and always have; the implementations, and the three-way disagreement between
 * them, are gone.
 *
 * `normalizeFoodName` is the MATCH key — descriptors dropped, so "2 cans of
 * diced tomatoes" finds "Tomatoes". That looseness makes it wrong for anything
 * that must not conflate two foods; those callers want normalizeFoodIdentity.
 */
export function normalizeFoodName(value) {
    return normalizeFoodMatch(value);
}
/**
 * IDENTITY key: the same folding, descriptors KEPT. "Tomatoes" and "tomato"
 * still meet; "Fresh Tomatoes" and "Canned Tomatoes" stay apart. Use this
 * wherever a mismatch would attribute one food's data to another (#697).
 */
export function normalizeFoodIdentity(value) {
    return canonicalFoodIdentity(value);
}
function unique(values) {
    return [...new Set(values.filter(Boolean))];
}
function displayDateNow(now) {
    return (now ?? new Date()).toISOString();
}
export function buildPantrySnapshot(input) {
    const now = input.now ?? new Date();
    const useSoonDays = input.useSoonDays ?? 7;
    const locations = new Map((input.locations ?? []).map((location) => [location.id, location.name]));
    const aliasesByProduct = new Map();
    const globalAliases = [];
    for (const alias of input.aliases ?? []) {
        if (alias.productId) {
            aliasesByProduct.set(alias.productId, [...(aliasesByProduct.get(alias.productId) ?? []), alias.name]);
        }
        else {
            globalAliases.push(alias);
        }
    }
    const lotsByProduct = new Map();
    for (const lot of input.lots) {
        if (["consumed", "discarded"].includes(lot.status ?? ""))
            continue;
        const state = expirationState(lot.expiresAt, lot.frozenAt, now);
        const useSoon = state === "expired" || state === "soon" || (state !== "frozen" && Boolean(lot.expiresAt && daysUntil(lot.expiresAt, now) <= useSoonDays));
        const snapshotLot = {
            id: lot.id,
            productId: lot.productId,
            quantity: Number(lot.quantity || 0),
            unit: lot.unit ?? "",
            expiresAt: lot.expiresAt ?? "",
            locationId: lot.locationId,
            locationName: lot.locationId ? locations.get(lot.locationId) ?? "" : "",
            expirationState: state,
            useSoon
        };
        lotsByProduct.set(lot.productId, [...(lotsByProduct.get(lot.productId) ?? []), snapshotLot]);
    }
    const productItems = input.products.map((product) => {
        const lots = lotsByProduct.get(product.id) ?? [];
        const quantity = lots.reduce((sum, lot) => sum + lot.quantity, 0);
        const productAliases = aliasesByProduct.get(product.id) ?? [];
        const normalizedNames = unique([
            normalizeFoodName(product.name),
            ...productAliases.map(normalizeFoodName),
            ...globalAliases
                .filter((alias) => normalizeFoodName(alias.canonicalName ?? "") === normalizeFoodName(product.name))
                .map((alias) => normalizeFoodName(alias.name))
        ]);
        return {
            productId: product.id,
            canonicalName: normalizeFoodName(product.name),
            displayName: product.name,
            normalizedNames,
            category: product.category ?? "",
            storeSection: product.storeSection ?? "",
            quantity,
            unit: product.defaultUnit ?? "",
            lots,
            staple: false,
            useSoon: lots.some((lot) => lot.useSoon)
        };
    });
    const productIds = new Set(productItems.map((item) => item.productId));
    const productStaples = new Set((input.staples ?? []).filter((staple) => staple.productId).map((staple) => staple.productId));
    for (const item of productItems) {
        if (item.productId && productStaples.has(item.productId))
            item.staple = true;
    }
    const manualStaples = (input.staples ?? [])
        .filter((staple) => !staple.productId || !productIds.has(staple.productId))
        .map((staple) => ({
        productId: staple.productId ?? null,
        canonicalName: normalizeFoodName(staple.name),
        displayName: staple.name,
        normalizedNames: [normalizeFoodName(staple.name)],
        category: staple.category ?? "",
        storeSection: "",
        quantity: null,
        unit: "",
        lots: [],
        staple: true,
        useSoon: false
    }));
    const items = [...productItems.filter((item) => (item.quantity ?? 0) > 0 || item.staple), ...manualStaples];
    return {
        generatedAt: displayDateNow(now),
        items,
        useSoon: items.filter((item) => item.useSoon),
        substitutions: input.substitutions ?? []
    };
}
function buildNameIndex(snapshot, includeStaples) {
    const byName = new Map();
    const byProductId = new Map();
    for (const item of snapshot.items) {
        if (item.staple && !includeStaples && item.quantity == null)
            continue;
        if (!item.staple && (item.quantity ?? 0) <= 0)
            continue;
        for (const name of item.normalizedNames) {
            if (name)
                byName.set(name, item);
        }
        if (item.canonicalName)
            byName.set(item.canonicalName, item);
        if (item.productId)
            byProductId.set(String(item.productId), item);
    }
    return { byName, byProductId };
}
function findByCategory(snapshot, category, includeStaples) {
    const normalizedCategory = normalizeFoodName(category ?? "");
    if (!normalizedCategory)
        return undefined;
    return snapshot.items.find((item) => {
        if (item.staple && !includeStaples && item.quantity == null)
            return false;
        if (!item.staple && (item.quantity ?? 0) <= 0)
            return false;
        return normalizeFoodName(item.category) === normalizedCategory;
    });
}
function normalizeUnit(value) {
    return (value ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "")
        .replace(/s$/, "");
}
function unitStatusFor(ingredient, item) {
    const ingredientUnit = normalizeUnit(ingredient.unit);
    if (!ingredientUnit)
        return "not_required";
    const pantryUnit = normalizeUnit(item.unit);
    if (!pantryUnit)
        return "unknown";
    return ingredientUnit === pantryUnit ? "exact" : "mismatch";
}
function quantityStatusFor(ingredient, item, unitStatus) {
    if (ingredient.quantity == null)
        return "not_required";
    if (item.quantity == null || unitStatus === "unknown" || unitStatus === "mismatch")
        return "unknown";
    return item.quantity >= ingredient.quantity ? "sufficient" : "insufficient";
}
function matchedIngredient(ingredient, pantryItem, status, availability, baseReason, substituteFor) {
    const unitStatus = unitStatusFor(ingredient, pantryItem);
    const quantityStatus = quantityStatusFor(ingredient, pantryItem, unitStatus);
    const nextStatus = quantityStatus === "insufficient" ? "insufficient" : status;
    const available = `${pantryItem.quantity ?? "unknown"} ${pantryItem.unit}`.trim();
    const required = `${ingredient.quantity ?? ""} ${ingredient.unit ?? ""}`.trim();
    const warnings = [
        availability === "assumed_staple" ? "stock is assumed, not confirmed" : "",
        quantityStatus === "unknown" && ingredient.quantity != null ? "quantity needs review" : "",
        quantityStatus === "insufficient" ? `only ${available} is confirmed for ${required}` : "",
        unitStatus === "mismatch" ? `unit mismatch: stock is ${pantryItem.unit || "unknown"}, recipe asks for ${ingredient.unit}` : "",
        unitStatus === "unknown" ? "stock unit is unknown" : ""
    ].filter(Boolean);
    return {
        ingredient,
        pantryItem,
        status: nextStatus,
        availability,
        quantityStatus,
        unitStatus,
        substituteFor,
        reason: warnings.length ? `${baseReason}; ${warnings.join("; ")}` : baseReason
    };
}
function matchIngredient(ingredient, snapshot, lookup, includeStaples, productIndex) {
    if (ingredient.optional) {
        return {
            ingredient,
            status: "optional",
            availability: "optional",
            quantityStatus: "not_required",
            unitStatus: "not_required",
            reason: "Optional ingredient"
        };
    }
    const normalized = ingredient.normalizedName ? normalizeFoodName(ingredient.normalizedName) : normalizeFoodName(ingredient.name);
    const productMatch = ingredient.productId
        ? snapshot.items.find((item) => item.productId === ingredient.productId && (includeStaples || !item.staple || item.quantity != null))
        : undefined;
    const direct = productMatch ?? lookup.byName.get(normalized);
    // A recipe line with a preparation clause stores its normalized name with the
    // clause still attached ("2 ripe mangoes, peeled and sliced" -> "ripe mango
    // peeled and"), which matches no pantry key, so the ingredient read as MISSING
    // even when the household held the food (#1099). The shopping path has solved
    // this since #931; resolveIngredientProductId IS that solution, reused rather
    // than reimplemented — it tries the exact identity first, then the STOCK strip
    // (never the shopping-label strip, which would fold "cream, for whipping" onto
    // a generic Cream), and returns nothing at all for an identity two products
    // both claim (#697). A second matcher here is exactly the duplication epic
    // #1034 exists to remove.
    const resolvedProductId = direct ? null : resolveIngredientProductId(ingredient, productIndex);
    const resolved = resolvedProductId ? lookup.byProductId.get(String(resolvedProductId)) : undefined;
    const matched = direct ?? resolved;
    if (matched) {
        const assumedStaple = matched.staple && matched.quantity == null;
        return matchedIngredient(ingredient, matched, assumedStaple ? "staple" : "in_stock", assumedStaple ? "assumed_staple" : "confirmed_stock", assumedStaple ? `${matched.displayName} is marked as a staple` : `${matched.displayName} is in stock`);
    }
    for (const substitution of snapshot.substitutions) {
        if (normalizeFoodName(substitution.ingredientName) !== normalized)
            continue;
        const substitute = lookup.byName.get(normalizeFoodName(substitution.substituteName));
        if (substitute) {
            return matchedIngredient(ingredient, substitute, "substituted", "substitution", `${substitute.displayName} can substitute for ${substitution.ingredientName}${substitution.notes ? ` (${substitution.notes})` : ""}`, substitution.ingredientName);
        }
    }
    const categoryMatch = findByCategory(snapshot, ingredient.category, includeStaples);
    if (categoryMatch) {
        return matchedIngredient(ingredient, categoryMatch, "category_match", "category_match", `${categoryMatch.displayName} fits the ${ingredient.category} category`);
    }
    return {
        ingredient,
        status: "missing",
        availability: "missing",
        quantityStatus: "insufficient",
        unitStatus: ingredient.unit ? "unknown" : "not_required",
        reason: `${ingredient.name} is missing`
    };
}
export function readinessForMissingCount(missingCount, confidence, hasTrustGaps = false) {
    if (confidence < 0.55)
        return "needs_review";
    if (missingCount === 0 && hasTrustGaps)
        return "almost_ready";
    if (missingCount === 0)
        return "ready";
    if (missingCount === 1)
        return "missing_one";
    if (missingCount <= 3)
        return "missing_two_or_three";
    return "almost_ready";
}
function needsTrustReview(match) {
    return match.availability === "assumed_staple"
        || match.availability === "category_match"
        || match.availability === "substitution"
        || match.quantityStatus === "unknown"
        || match.unitStatus === "mismatch"
        || match.unitStatus === "unknown";
}
function missingShoppingQuantity(match) {
    if (match.ingredient.quantity == null)
        return null;
    if (match.status !== "insufficient" || !match.pantryItem || match.quantityStatus !== "insufficient")
        return match.ingredient.quantity;
    return Number(Math.max(0, match.ingredient.quantity - (match.pantryItem.quantity ?? 0)).toFixed(4));
}
export function matchMealCandidate(candidate, snapshot, options = {}) {
    const includeStaples = options.includeStaples ?? true;
    const lookup = buildNameIndex(snapshot, includeStaples);
    const matches = candidate.ingredients.map((ingredient) => matchIngredient(ingredient, snapshot, lookup, includeStaples, options.productIndex));
    const missing = matches.filter((match) => match.status === "missing" || match.status === "insufficient");
    const have = matches.filter((match) => match.status !== "missing" && match.status !== "insufficient");
    const expiringSoon = unique(have
        .map((match) => match.pantryItem)
        .filter((item) => Boolean(item?.useSoon))
        .map((item) => item.displayName)).map((name) => have.find((match) => match.pantryItem?.displayName === name).pantryItem);
    const confidence = candidate.confidence ?? Math.min(1, Math.max(0.6, matches.length ? have.length / matches.length : 0.5));
    const hasTrustGaps = have.some(needsTrustReview);
    const readiness = readinessForMissingCount(missing.length, confidence, hasTrustGaps);
    const totalMinutes = Number(candidate.prepMinutes ?? 0) + Number(candidate.cookMinutes ?? 0);
    const sourceBoost = candidate.sourceType === "saved_recipe" ? 14 : candidate.sourceType === "simple_meal" ? 10 : candidate.sourceType === "template" ? 4 : 0;
    const missingPenalty = missing.length * 26 + (hasTrustGaps ? 10 : 0);
    const useSoonBoost = expiringSoon.length * (options.prioritizeExpiring === false ? 5 : 14);
    const quickBoost = totalMinutes > 0 && totalMinutes <= 20 ? 8 : totalMinutes <= 35 ? 4 : 0;
    const score = Math.round(60 + sourceBoost + confidence * 20 + have.length * 4 + useSoonBoost + quickBoost - missingPenalty);
    const assumedStaples = have.filter((match) => match.availability === "assumed_staple");
    const quantityUnknown = have.filter((match) => match.quantityStatus === "unknown");
    const unitMismatches = have.filter((match) => match.unitStatus === "mismatch");
    const insufficient = missing.filter((match) => match.status === "insufficient");
    const badges = [
        readiness === "ready" ? "Ready from confirmed stock" : readiness === "almost_ready" && missing.length === 0 ? "Verify pantry assumptions" : readiness === "missing_one" ? "Need 1 item" : readiness === "missing_two_or_three" ? `Need ${missing.length} items` : "Review",
        assumedStaples.length ? "Assumes staples" : "",
        insufficient.length ? "Missing exact quantity" : "",
        quantityUnknown.length ? "Quantity uncertain" : "",
        unitMismatches.length ? "Unit mismatch" : "",
        candidate.sourceType === "saved_recipe" ? "Saved recipe" : candidate.sourceType === "catalog_recipe" ? "Catalog" : candidate.sourceType === "simple_meal" ? "Simple dinner" : candidate.sourceType === "llm" ? "LLM idea" : "Flexible meal",
        totalMinutes ? `${totalMinutes} minutes` : "",
        expiringSoon[0] ? `Uses expiring ${expiringSoon[0].displayName}` : ""
    ].filter(Boolean);
    const why = [
        readiness === "ready" ? "Every required ingredient is backed by confirmed stock." : missing.length === 0 ? "All required ingredients matched, but at least one match needs quantity, unit, staple, substitution, or category review." : `${have.length} ingredient${have.length === 1 ? "" : "s"} matched and ${missing.length} need shopping or more exact quantity.`,
        expiringSoon.length ? `Prioritizes ${expiringSoon.map((item) => item.displayName).join(", ")} before it expires.` : "",
        have.some((match) => match.status === "substituted") ? "Includes an allowed substitution." : "",
        assumedStaples.length ? `Assumes household staple${assumedStaples.length === 1 ? "" : "s"}: ${assumedStaples.map((match) => match.pantryItem?.displayName).filter(Boolean).join(", ")}.` : "",
        quantityUnknown.length ? "One or more matched ingredients have uncertain quantity." : "",
        unitMismatches.length ? "One or more matched ingredients use a different unit than the recipe asks for." : "",
        candidate.confidence != null && candidate.confidence < 0.7 ? "This is a lower-confidence idea and should be reviewed." : ""
    ].filter(Boolean);
    return {
        id: candidate.id,
        sourceType: candidate.sourceType,
        title: candidate.title,
        description: candidate.description ?? "",
        sourceUrl: candidate.sourceUrl ?? "",
        imageUrl: candidate.imageUrl ?? "",
        servings: candidate.servings ?? null,
        prepMinutes: Number(candidate.prepMinutes ?? 0),
        cookMinutes: Number(candidate.cookMinutes ?? 0),
        totalMinutes,
        tags: candidate.tags ?? [],
        cuisine: candidate.cuisine ?? "",
        instructions: candidate.instructions ?? "",
        confidence,
        readiness,
        score,
        badges,
        why,
        have,
        missing,
        expiringSoon,
        likelyConsumes: have
            .filter((match) => match.pantryItem)
            .map((match) => ({
            ingredientName: match.ingredient.name,
            productId: match.pantryItem?.productId,
            productName: match.pantryItem?.displayName ?? match.ingredient.name,
            quantity: match.ingredient.quantity ?? null,
            unit: match.ingredient.unit ?? match.pantryItem?.unit ?? "",
            lotIds: match.pantryItem?.lots.map((lot) => lot.id) ?? []
        })),
        shoppingItems: missing.map((match) => ({
            productId: match.ingredient.productId ?? null,
            name: match.ingredient.name,
            quantity: missingShoppingQuantity(match),
            unit: match.ingredient.unit ?? "",
            notes: match.status === "insufficient" ? `Need more exact quantity for ${candidate.title}` : `Missing for ${candidate.title}`
        }))
    };
}
export function generateMealSuggestions(candidates, snapshot, options = {}) {
    const maxMissing = options.maxMissingItems ?? 3;
    const normalizedTags = (options.tags ?? []).map(normalizeFoodName).filter(Boolean);
    const filtered = candidates.filter((candidate) => {
        if (options.simpleMealsOnly && candidate.sourceType !== "simple_meal")
            return false;
        if (options.recipesOnly && !["saved_recipe", "catalog_recipe"].includes(candidate.sourceType))
            return false;
        if (candidate.sourceType === "catalog_recipe" && options.includeCatalog === false)
            return false;
        if (candidate.sourceType === "llm" && options.includeLlm === false)
            return false;
        if (options.maxCookMinutes != null && Number(candidate.cookMinutes ?? 0) > options.maxCookMinutes)
            return false;
        if (normalizedTags.length) {
            const tags = (candidate.tags ?? []).map(normalizeFoodName);
            if (!normalizedTags.every((tag) => tags.includes(tag)))
                return false;
        }
        return true;
    });
    return filtered
        .map((candidate) => matchMealCandidate(candidate, snapshot, options))
        .filter((suggestion) => suggestion.missing.length <= maxMissing || suggestion.readiness === "needs_review")
        .sort((a, b) => b.score - a.score || a.missing.length - b.missing.length || a.title.localeCompare(b.title));
}
